import { Menu, MenuItem, type MenuProps, Scrollable } from '@hive/component';
import { useAsyncCallback } from '@hive/core/components/hooks/hive-async-hooks';
import { inferOpenMode } from '@hive/core/utils';
import { useI18n } from '@hive/i18n';
import { InformationIcon, TemplateIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { type DocRecord, DocsService } from '../../doc';
import { DocDisplayMetaService } from '../../doc-display-meta';
import { WorkbenchLink, WorkbenchService } from '../../workbench';
import { TemplateDocService } from '../services/template-doc';
import * as styles from './styles.css';
interface CommonProps {
  onSelect?: (docId: string) => void;
  asLink?: boolean;
}

interface DocItemProps extends CommonProps {
  doc: DocRecord;
}

const DocItem = ({ doc, onSelect, asLink }: DocItemProps) => {
  const docDisplayService = useService(DocDisplayMetaService);
  const Icon = useLiveData(docDisplayService.icon$(doc.id));
  const title = useLiveData(docDisplayService.title$(doc.id));

  const onClick = useAsyncCallback(async () => {
    onSelect?.(doc.id);
  }, [doc.id, onSelect]);

  const menuItem = (
    <MenuItem
      prefixIcon={<Icon />}
      onClick={onClick}
      data-testid={`template-doc-item-${doc.id}`}
    >
      {title}
    </MenuItem>
  );

  if (asLink) {
    return <WorkbenchLink to={`/${doc.id}`}>{menuItem}</WorkbenchLink>;
  }
  return menuItem;
};

const Empty = () => {
  const t = useI18n();
  return (
    <div className={styles.empty}>
      <InformationIcon className={styles.emptyIcon} />
      <div className={styles.emptyContent}>
        <span className={styles.emptyText}>
          {t['com.hive.template-list.empty']()}
        </span>
        <span className={styles.emptyHint}>
          {t['com.hive.template-list.empty-hint']()}
        </span>
      </div>
    </div>
  );
};

interface TemplateListMenuContentProps extends CommonProps {
  prefixItems?: React.ReactNode;
  suffixItems?: React.ReactNode;
}
export const TemplateListMenuContent = ({
  prefixItems,
  suffixItems,
  ...props
}: TemplateListMenuContentProps) => {
  const templateDocService = useService(TemplateDocService);
  const [templateDocs] = useState(() =>
    templateDocService.list.getTemplateDocs()
  );

  return (
    <ul className={styles.list}>
      {prefixItems}
      {templateDocs.length ? (
        templateDocs.map(doc => <DocItem key={doc.id} doc={doc} {...props} />)
      ) : (
        <Empty />
      )}
      {suffixItems}
    </ul>
  );
};

export const TemplateListMenuContentScrollable = (
  props: TemplateListMenuContentProps
) => {
  return (
    <Scrollable.Root>
      <Scrollable.Scrollbar />
      <Scrollable.Viewport className={styles.scrollableViewport}>
        <TemplateListMenuContent {...props} />
      </Scrollable.Viewport>
    </Scrollable.Root>
  );
};

interface TemplateListMenuProps
  extends TemplateListMenuContentProps,
    Omit<MenuProps, 'items'> {}
export const TemplateListMenu = ({
  children,
  onSelect,
  asLink,
  prefixItems,
  suffixItems,
  contentOptions,
  ...otherProps
}: TemplateListMenuProps) => {
  return (
    <Menu
      items={
        <TemplateListMenuContentScrollable
          onSelect={onSelect}
          asLink={asLink}
          prefixItems={prefixItems}
          suffixItems={suffixItems}
        />
      }
      contentOptions={{
        ...contentOptions,
        className: styles.menuContent,
      }}
      {...otherProps}
    >
      {children}
    </Menu>
  );
};

export const TemplateListMenuAdd = () => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  const createNewTemplate = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const record = docsService.createDoc({ isTemplate: true });
      workbench.openDoc(record.id, { at: inferOpenMode(e) });
    },
    [docsService, workbench]
  );

  return (
    <MenuItem
      data-testid="template-doc-item-create"
      prefixIcon={<TemplateIcon />}
      onClick={createNewTemplate}
      onAuxClick={createNewTemplate}
    >
      {t['com.hive.template-list.create-new']()}
    </MenuItem>
  );
};
